import { CobaltDivider } from '@cae/cobalt-react/components/divider';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { classNames } from '@cae/cobalt-react/utils';
import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { useHasPermission } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import * as React from 'react';

import type { HelpPanelProps } from './HelpPanel.types';

import { useContacts } from '../../hooks/use-contacts/useContacts';
import { AeContactSection } from '../ae-contact-section/AeContactSection';
import { BasicHelpSection } from '../basic-help-section/BasicHelpSection';
import { InternalHelpSection } from '../internal-help-section/InternalHelpSection';
import styles from './HelpPanel.module.css';

const HelpPanel = React.memo(
  ({ onSubmitRequest }: Readonly<HelpPanelProps>): React.ReactElement => {
    const hasAeContactPermission = useHasPermission('HelpAEContact_View_Org');
    const hasBasicPermission = useHasPermission('HelpBasic_View_Any');
    const hasInternalPermission = useHasPermission('HelpInternal_View_Any');
    const isMobile = useMediaQuery(MediaQuery.Mobile);

    const { selectedOrganization } = useOrganizations();
    const { selectedModule } = useModules();

    const isManageModule = selectedModule?.name === 'Manage';
    const showAeContact =
      hasAeContactPermission && !!selectedOrganization && isManageModule;

    const {
      isError: isContactsError,
      isLoading: isContactsLoading,
      data: contactData,
    } = useContacts({
      organizationId: selectedOrganization?.id ?? '',
      enabled: showAeContact,
    });

    const showAeContactSection =
      showAeContact &&
      !isContactsError &&
      !isContactsLoading &&
      !!contactData?.name;

    const panelClassNames = classNames(
      styles.panel,
      isMobile && styles.panelMobile
    );

    if (isContactsLoading) {
      return (
        <div className={styles.loaderPanel} data-testid="help-panel-loader">
          <ContentLoader />
        </div>
      );
    }

    return (
      <div className={panelClassNames} data-testid="help-panel">
        {!isMobile && <h2 className="heading-02">Help</h2>}

        {showAeContactSection && (
          <>
            <AeContactSection
              contact={contactData}
              onSubmitRequest={onSubmitRequest}
            />
            {(hasBasicPermission || hasInternalPermission) && (
              <CobaltDivider color="subtle" />
            )}
          </>
        )}

        {hasBasicPermission && <BasicHelpSection />}

        {hasInternalPermission && <InternalHelpSection />}
      </div>
    );
  }
);

HelpPanel.displayName = 'HelpPanel';

export { HelpPanel };
